import React, { useEffect, useState } from 'react';
import { Text, View, Platform } from 'react-native';
import { LineChart } from 'react-native-chart-kit';
import * as Animatable from 'react-native-animatable';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import StyledLineChart from './Charts/LineChart';
import { ChartConfig } from 'react-native-chart-kit/dist/HelperTypes';
import DataSetChart from './Charts/DataSetChart';
import SmallActionButton, { ActionButtonProps } from '@components/UI/SmallActionButton';
import { ViewProps } from 'tamagui';
import { LinearGradient } from 'expo-linear-gradient';
import LottieView from "lottie-react-native";
import styles from '@stylesheet';

interface Chart {
	reportData?: any;
	chartTitle?: string;
	state?: string;
	withActions?: boolean;
	transparent?: boolean;
	style?: ViewProps;
	hideHeader?: boolean;
	hideLabels?: boolean;
	chartOnly?: boolean;
	paddingHorizontal?: number;
}

const actions: ActionButtonProps[] = [
	{
		action: {
			function: () => {
				// Refresh functionality can be implemented here
				console.log('Chart refresh requested');
			},
			icon: 'refresh',
			color: 'Refresh',
		}
	},
]

const DataChart = ({ transparent, hideHeader, hideLabels = false, chartOnly = false, style, withActions = false, reportData, chartTitle = "", state = "month", paddingHorizontal }: Chart) => {
	const [chartParentWidth, setChartParentWidth] = useState(570);
	const [activeData, setActiveData] = useState<any>();
	const [chartParentHeight, setChartParentHeight] = useState(0);
	const [activeState, setActiveState] = useState("");
	const theme = themeSelector();
	const [chartType, setChartType] = useState('Line');
	const [containerDimensions, setContainerDimensions] = useState({ width: 0, height: 0 });
	const [showLoadingTimeout, setShowLoadingTimeout] = useState(true);
	const [isInitialLoad, setIsInitialLoad] = useState(true);

	useEffect(() => {
		setActiveData(reportData?.[state.toLowerCase()]);
		if (reportData?.[state.toLowerCase()]) {
			setIsInitialLoad(false);
		}
	}, [reportData, state]);

	// Set timeout for loading state
	useEffect(() => {
		const timeout = setTimeout(() => {
			setShowLoadingTimeout(false);
		}, 5000);

		return () => clearTimeout(timeout);
	}, []);

	// Reset loading timeout when data changes
	useEffect(() => {
		if (reportData) {
			setShowLoadingTimeout(true);
			setIsInitialLoad(false);
		}
	}, [reportData]);

	// Loading state
	if (!activeData && (showLoadingTimeout || isInitialLoad)) {
		return (
			<Animatable.View
				animation="fadeIn"
				duration={300}
				style={[
					styles.modernCard,
					{
						maxHeight: 280,
						justifyContent: 'center',
						alignItems: 'center',
						gap: 16,
						backgroundColor: theme ? 'rgba(255,255,255,0.95)' : 'rgba(30,30,30,0.95)',
						borderColor: theme ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.08)',
						borderWidth: 1,
						borderRadius: 24,
						flex: 1,
						...(Platform.OS === 'web' && {
							boxShadow: theme
								? '0 8px 32px rgba(0,0,0,0.08)'
								: '0 8px 32px rgba(0,0,0,0.3)',
						}),
						...style as any
					}
				]}
			>
				<View style={{
					width: 80,
					height: 80,
					backgroundColor: theme ? 'rgba(90,192,234,0.1)' : 'rgba(90,192,234,0.2)',
					borderRadius: 40,
					justifyContent: 'center',
					alignItems: 'center'
				}}>
					<LottieView
						style={{ height: 50, width: 50 }}
						source={require("@assets/animations/spin-leaf-loader.json")}
						autoPlay
						loop
					/>
				</View>
				<Text style={{
					color: theme ? '#64748b' : '#94a3b8',
					textAlign: 'center',
					fontSize: 14,
					fontWeight: '500'
				}}>
					Loading chart data...
				</Text>
				<Text style={{
					color: theme ? '#94a3b8' : '#64748b',
					textAlign: 'center',
					fontSize: 12
				}}>
					Please wait while we fetch your analytics
				</Text>
			</Animatable.View>
		);
	}

	// No data state (after timeout or when data is explicitly empty)
	if (!activeData) {
		return (
			<Animatable.View
				animation="fadeIn"
				duration={300}
				style={[
					styles.modernCard,
					{
						maxHeight: 180,
						transform: [{ scale: 0.8 }],
						transformOrigin: 'right',
						justifyContent: 'center',
						alignItems: 'center',
						gap: 16,
						backgroundColor: theme ? 'rgba(255,255,255,0.95)' : 'rgba(30,30,30,0.95)',
						borderColor: theme ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.08)',
						borderWidth: 1,
						borderRadius: 24,
						flex: 1,
						...(Platform.OS === 'web' && {
							boxShadow: theme
								? '0 8px 32px rgba(0,0,0,0.08)'
								: '0 8px 32px rgba(0,0,0,0.3)',
						}),
						...style as any
					}
				]}
			>
				<View style={{
					width: 80,
					height: 80,
					backgroundColor: theme ? 'rgba(156,163,175,0.1)' : 'rgba(156,163,175,0.2)',
					borderRadius: 40,
					justifyContent: 'center',
					alignItems: 'center'
				}}>
					<Text style={{
						fontSize: 24,
						color: theme ? '#9ca3af' : '#6b7280'
					}}>
						📊
					</Text>
				</View>
				<Text style={{
					color: theme ? '#64748b' : '#94a3b8',
					textAlign: 'center',
					fontSize: 14,
					fontWeight: '500'
				}}>
					No data available
				</Text>
				<Text style={{
					color: theme ? '#94a3b8' : '#64748b',
					textAlign: 'center',
					fontSize: 12
				}}>
					Chart data is not available for the selected period
				</Text>
			</Animatable.View>
		);
	}

	return (
		<Animatable.View
			animation="slideInUp"
			duration={400}
			delay={100}
			style={{
				flex: 1,
				flexDirection: "row",
				...(chartOnly && { width: '100%', height: '100%', overflow: 'hidden' })
			}}
			onLayout={chartOnly ? ({ nativeEvent }) => {
				setContainerDimensions({
					width: nativeEvent.layout.width,
					height: nativeEvent.layout.height
				});
			} : undefined}
		>
			<View style={{
				minWidth: "100%",
				maxWidth: "100%",
				flex: 5,
				backgroundColor: chartOnly ? 'transparent' : (theme ? 'rgba(255,255,255,0.95)' : 'rgba(30,30,30,0.95)'),
				borderRadius: chartOnly ? 0 : 24,
				padding: chartOnly ? 0 : 20,
				borderWidth: chartOnly ? 0 : 1,
				borderColor: chartOnly ? 'transparent' : (theme ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.08)'),
				overflow: 'hidden',
				...(Platform.OS === 'web' && !chartOnly && {
					boxShadow: theme
						? '0 8px 32px rgba(0,0,0,0.08)'
						: '0 8px 32px rgba(0,0,0,0.3)',
				}),
				...style as any
			}}>
				{/* Header with gradient background */}
				{!hideHeader && !chartOnly && <LinearGradient
					colors={theme
						? ['rgba(90,192,234, 0.05)', 'rgba(90,192,234, 0.02)']
						: ['rgba(90,192,234, 0.15)', 'rgba(90,192,234, 0.05)']
					}
					start={{ x: 0, y: 0 }}
					end={{ x: 1, y: 1 }}
					style={{
						position: 'absolute',
						top: 0,
						left: 0,
						right: 0,
						height: 60,
						borderTopLeftRadius: 24,
						borderTopRightRadius: 24
					}}
				/>}
				{chartTitle && !hideHeader && !chartOnly && (
					<View style={{ marginBottom: 16, zIndex: 1 }}>
						<Text style={{
							fontSize: 18,
							fontWeight: '700',
							color: theme ? '#1e293b' : '#f1f5f9',
							marginBottom: 4
						}}>
							{chartTitle}
						</Text>
						<Text style={{
							fontSize: 12,
							color: theme ? '#64748b' : '#94a3b8'
						}}>
							{state.charAt(0).toUpperCase() + state.slice(1)} view • {activeData?.data?.length || 0} data points
						</Text>
					</View>
				)}

				<DataSetChart
					chartType={state === 'day' ? 'datetime' : state}
					chartTitle={chartTitle}
					title={chartTitle}
					subheader={""}
					minHeight={chartOnly ? (containerDimensions.height || 200) : 232}
					dotLabel={"entries"}
					dateCallback={() => { }}
					defaultDates={{ startDate: new Date(), endDate: new Date() }}
					controls={<></>}
					chartOnly={chartOnly}
					chart={{
						colors: ["rgba(90,192,234,1)", "rgba(90,192,234,.5)", "rgba(90,192,234,1)"],
						labels: activeData?.labels || [],
						series: [{
							name: "Data",
							data: activeData?.data || [],
							fill: 'gradient'
						}],
						options: {
							chart: {
								background: (transparent || chartOnly) ? "transparent" : (theme ? "rgba(255,255,255,0.8)" : "rgba(30,30,30,0.8)"),
								theme: theme ? 'light' : 'dark',
								toolbar: {
									show: !(hideLabels || chartOnly)
								}
							},
							legend: {
								show: !(hideLabels || chartOnly)
							},
							stroke: {
								curve: "smooth",
							},
							fill: {
								type: "gradient",
								gradient: {
									shade: theme ? 'light' : 'dark',
									type: "vertical",
									shadeIntensity: 0.5,
									gradientToColors: ["rgba(90,192,234,0.1)"],
									inverseColors: false,
									opacityFrom: 0.8,
									opacityTo: 0.1,
								}
							},
							grid: {
								show: !(hideLabels || chartOnly),
								borderColor: theme ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)',
							},
							xaxis: {
								labels: {
									show: !(hideLabels || chartOnly),
									style: {
										colors: theme ? '#64748b' : '#94a3b8'
									}
								}
							},
							yaxis: {
								labels: {
									show: !(hideLabels || chartOnly),
									style: {
										colors: theme ? '#64748b' : '#94a3b8'
									}
								}
							},
							tooltip: {
								theme: theme ? 'light' : 'dark'
							}
						}
					}}
				/>
			</View>

			{/* {withActions && actions.length > 0 && (
				<View style={{
					flex: 1,
					zIndex: -1,
					position: "relative",
					borderTopEndRadius: 20,
					borderBottomEndRadius: 20,
					backgroundColor: transparent ? "transparent" : (theme ? "rgba(248,250,252,0.9)" : "rgba(20,20,20,0.9)"),
					borderWidth: 1,
					borderColor: theme ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.05)',
					left: -10,
					minWidth: 80,
					maxWidth: 80,
					marginVertical: "2%",
					...(Platform.OS === 'web' && {
						boxShadow: theme
							? '0 4px 16px rgba(0,0,0,0.06)'
							: '0 4px 16px rgba(0,0,0,0.2)',
					})
				}}>
					{actions.map((action, index) => (
						<SmallActionButton action={action.action} key={index} />
					))}
				</View>
			)} */}
		</Animatable.View>
	)

};

export default DataChart;
